import { useState, useRef, useCallback, useEffect } from "react";
import { DiffEditor, Editor, type Monaco, type DiffOnMount, type OnMount } from "@monaco-editor/react";
import type { languages, IRange, editor, Position, MarkerSeverity } from "monaco-editor";
import resumeSchema from "../schemas/yamlresume.schema.json";
import { parse as parseYAML, YAMLParseError } from "yaml";
import Ajv2020, { type ErrorObject } from "ajv/dist/2020";
import addFormats from "ajv-formats";
import { TopBar, type EditingSource, type CompareSource } from "./TopBar";
import { DEFAULT_RESUME_CONTENT } from "@/constants";
import {
  getStorage,
  saveChanges,
  saveNewVersion,
  getVersionById,
  getVersions,
  hasLastSaved,
  type VersionEntry,
} from "@/lib/storage";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Initialize AJV with JSON Schema 2020-12 support
const ajv = new Ajv2020({
  allErrors: true,      // Report all errors, not just the first one
  verbose: true,        // Include schema and data in errors
  strict: false,        // Relaxed mode for complex schemas
});
addFormats(ajv);

// Preprocess schema: convert legacy "id" to "$id" in $defs
function preprocessSchema(schema: typeof resumeSchema) {
  const processed = JSON.parse(JSON.stringify(schema));
  if (processed.$defs) {
    for (const key of Object.keys(processed.$defs)) {
      const def = processed.$defs[key as keyof typeof processed.$defs] as Record<string, unknown>;
      if (def && "id" in def) {
        // Remove legacy "id" field (not needed for validation)
        delete def.id;
      }
    }
  }
  return processed;
}

// Compile the schema once at module level for performance
const validateSchema = ajv.compile(preprocessSchema(resumeSchema));

interface ValidationError {
  line: number;
  column: number;
  endLine?: number;
  endColumn?: number;
  message: string;
  severity: "error" | "warning";
}

/**
 * Parse YAML and validate against schema
 * Returns array of validation errors with line/column positions
 */
function validateYAML(yamlContent: string): ValidationError[] {
  const errors: ValidationError[] = [];

  // Step 1: Try to parse YAML (syntax validation)
  let parsedData: unknown;
  try {
    parsedData = parseYAML(yamlContent, {
      prettyErrors: true,  // Better error messages
      strict: true,        // Strict YAML parsing
    });
  } catch (e) {
    if (e instanceof YAMLParseError) {
      // Extract line/column from YAML parse error
      const pos = e.linePos?.[0] ?? { line: 1, col: 1 };
      errors.push({
        line: pos.line,
        column: pos.col,
        endLine: e.linePos?.[1]?.line ?? pos.line,
        endColumn: e.linePos?.[1]?.col ?? pos.col + 10,
        message: `YAML Syntax Error: ${e.message.split("\n")[0]}`,
        severity: "error",
      });
    } else {
      errors.push({
        line: 1,
        column: 1,
        message: `Parse Error: ${e instanceof Error ? e.message : String(e)}`,
        severity: "error",
      });
    }
    return errors; // Can't continue to schema validation if YAML is invalid
  }

  // Step 2: Validate against JSON Schema
  if (parsedData === null || parsedData === undefined) {
    errors.push({
      line: 1,
      column: 1,
      message: "Document is empty",
      severity: "warning",
    });
    return errors;
  }

  const isValid = validateSchema(parsedData);
  
  if (!isValid && validateSchema.errors) {
    // Convert AJV errors to our ValidationError format
    for (const err of validateSchema.errors) {
      const { line, column } = findPositionForPath(yamlContent, err.instancePath);
      errors.push({
        line,
        column,
        message: formatSchemaError(err),
        severity: "error",
      });
    }
  }

  return errors;
}

/**
 * Format AJV error into a human-readable message
 */
function formatSchemaError(error: ErrorObject): string {
  const path = error.instancePath || "(root)";
  
  switch (error.keyword) {
    case "required":
      return `Missing required property: "${error.params.missingProperty}" at ${path}`;
    case "additionalProperties":
      return `Unknown property: "${error.params.additionalProperty}" at ${path}`;
    case "type":
      return `Type error at ${path}: expected ${error.params.type}`;
    case "enum":
      return `Invalid value at ${path}: must be one of [${error.params.allowedValues?.join(", ")}]`;
    case "minLength":
      return `Value at ${path} is too short (minimum ${error.params.limit} characters)`;
    case "maxLength":
      return `Value at ${path} is too long (maximum ${error.params.limit} characters)`;
    case "pattern":
      return `Invalid format at ${path}: does not match required pattern`;
    case "format":
      return `Invalid format at ${path}: expected ${error.params.format}`;
    case "minimum":
    case "maximum":
      return `Value at ${path} is out of range`;
    default:
      return `Schema error at ${path}: ${error.message || error.keyword}`;
  }
}

/**
 * Find the line/column position in YAML for a JSON path like "/content/basics/name"
 */
function findPositionForPath(yamlContent: string, jsonPath: string): { line: number; column: number } {
  if (!jsonPath || jsonPath === "") {
    return { line: 1, column: 1 };
  }

  const lines = yamlContent.split("\n");
  const pathParts = jsonPath.split("/").filter(Boolean);
  
  // Build regex patterns to find each path segment
  let currentLine = 1;
  let lastMatchLine = 1;
  let lastMatchColumn = 1;

  for (const part of pathParts) {
    // Skip array indices for now, focus on finding keys
    if (/^\d+$/.test(part)) continue;
    
    // Look for this key in the remaining lines
    const keyPattern = new RegExp(`^\\s*-?\\s*${escapeRegex(part)}\\s*:`);
    
    for (let i = currentLine - 1; i < lines.length; i++) {
      const line = lines[i];
      if (line && keyPattern.test(line)) {
        lastMatchLine = i + 1;
        lastMatchColumn = (line.match(/^\s*/)?.[0]?.length ?? 0) + 1;
        currentLine = i + 2; // Start searching after this line for next part
        break;
      }
    }
  }

  return { line: lastMatchLine, column: lastMatchColumn };
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Apply validation errors to Monaco editor as markers
 */
function setMonacoMarkers(
  monaco: Monaco,
  model: editor.ITextModel,
  errors: ValidationError[]
) {
  const markers = errors.map((err) => ({
    startLineNumber: err.line,
    startColumn: err.column,
    endLineNumber: err.endLine ?? err.line,
    endColumn: err.endColumn ?? model.getLineLength(err.line) + 1,
    message: err.message,
    severity: err.severity === "error" 
      ? monaco.MarkerSeverity.Error 
      : monaco.MarkerSeverity.Warning,
    source: "YAMLResume Schema",
  }));

  monaco.editor.setModelMarkers(model, "yamlresume-validator", markers);
}

type SchemaProperty = {
  type?: string;
  description?: string;
  title?: string;
  examples?: unknown[];
  properties?: Record<string, SchemaProperty>;
  items?: { type?: string; properties?: Record<string, SchemaProperty> };
  anyOf?: Array<{
    type?: string;
    $ref?: string;
    examples?: unknown[];
    items?: { type?: string; properties?: Record<string, SchemaProperty> };
  }>;
};

/**
 * Get the items properties from a schema property, handling anyOf arrays
 */
function getItemsProperties(prop: SchemaProperty): Record<string, SchemaProperty> | null {
  // Direct items
  if (prop.items?.properties) {
    return prop.items.properties as Record<string, SchemaProperty>;
  }
  // Items inside anyOf (like work section)
  if (prop.anyOf) {
    for (const variant of prop.anyOf) {
      if (variant.type === "array" && variant.items?.properties) {
        return variant.items.properties as Record<string, SchemaProperty>;
      }
    }
  }
  return null;
}

function getIndentLevel(line: string): number {
  const match = line.match(/^(\s*)/);
  return match?.[1]?.length ?? 0;
}

function getCurrentContext(text: string, currentLineNumber: number, currentIndent: number): string[] {
  const lines = text.split("\n");
  const path: string[] = [];
  const indentStack: number[] = [];

  for (let i = 0; i < currentLineNumber; i++) {
    const line = lines[i];
    if (!line || !line.trim() || line.trim().startsWith("#")) continue;

    const indent = getIndentLevel(line);
    // Match keys that open a block (key followed by : and either nothing or just whitespace)
    const keyMatch = line.match(/^\s*-?\s*([a-zA-Z_][a-zA-Z0-9_]*):\s*$/);

    if (keyMatch?.[1]) {
      // This is a key that opens a nested block (no value on same line)
      const key = keyMatch[1];
      
      // Pop path until we're at the right level
      while (indentStack.length > 0 && indent <= indentStack[indentStack.length - 1]!) {
        path.pop();
        indentStack.pop();
      }
      
      path.push(key);
      indentStack.push(indent);
    } else {
      // This is a key with a value on the same line - check if we need to pop
      const simpleKeyMatch = line.match(/^\s*-?\s*([a-zA-Z_][a-zA-Z0-9_]*):/);
      if (simpleKeyMatch) {
        const lineIndent = getIndentLevel(line);
        // Pop any parent contexts that are at the same or greater indent
        while (indentStack.length > 0 && lineIndent <= indentStack[indentStack.length - 1]!) {
          path.pop();
          indentStack.pop();
        }
      }
    }
  }

  // Now check: are we still inside the last context based on our current indent?
  while (indentStack.length > 0 && currentIndent <= indentStack[indentStack.length - 1]!) {
    path.pop();
    indentStack.pop();
  }

  return path;
}

function getPropertyType(prop: SchemaProperty): string | undefined {
  if (prop.type) return prop.type;
  // Handle anyOf schemas (optional fields)
  if (prop.anyOf) {
    const nonNullType = prop.anyOf.find(p => p.type && p.type !== "null");
    if (nonNullType?.type) return nonNullType.type;
  }
  return undefined;
}

function getSchemaPropertiesAtPath(
  schema: typeof resumeSchema,
  path: string[]
): Record<string, SchemaProperty> | null {
  let current: Record<string, SchemaProperty> | undefined = schema.properties as Record<string, SchemaProperty>;

  for (const key of path) {
    if (!current || !current[key]) return null;

    const prop: SchemaProperty = current[key];
    const propType = getPropertyType(prop);
    
    if (propType === "object" && prop.properties) {
      current = prop.properties as Record<string, SchemaProperty>;
    } else if (propType === "array") {
      // Handle both direct items and items inside anyOf
      const itemProps = getItemsProperties(prop);
      if (itemProps) {
        current = itemProps;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  return current ?? null;
}

function createCompletionProposals(
  monaco: Monaco,
  range: IRange,
  properties: Record<string, SchemaProperty>,
  existingKeys: Set<string>
): languages.CompletionItem[] {
  return Object.entries(properties)
    .filter(([key]) => !existingKeys.has(key))
    .map(([key, prop]) => {
      let insertText = `${key}: `;
      let insertTextRules: languages.CompletionItemInsertTextRule | undefined;
      const propType = getPropertyType(prop);

      if (propType === "array") {
        insertText = `${key}:\n  - \${1:item}`;
        insertTextRules = monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet;
      } else if (propType === "object") {
        insertText = `${key}:\n  \${1:key}: \${2:value}`;
        insertTextRules = monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet;
      } else if (propType === "number" || propType === "integer") {
        insertText = `${key}: \${1:0}`;
        insertTextRules = monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet;
      } else {
        insertText = `${key}: \${1:}`;
        insertTextRules = monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet;
      }

      // Use title or description for documentation
      const documentation = prop.description || prop.title || `${key} field`;

      return {
        label: key,
        kind: monaco.languages.CompletionItemKind.Property,
        documentation,
        insertText,
        insertTextRules,
        range,
      };
    });
}

function getExistingKeysAtLevel(text: string, targetIndent: number): Set<string> {
  const lines = text.split("\n");
  const keys = new Set<string>();

  for (const line of lines) {
    if (!line || !line.trim() || line.trim().startsWith("#")) continue;

    const indent = getIndentLevel(line);
    if (indent === targetIndent) {
      const keyMatch = line.match(/^\s*([a-zA-Z_][a-zA-Z0-9_]*):/);
      if (keyMatch?.[1]) {
        keys.add(keyMatch[1]);
      }
    }
  }

  return keys;
}

/**
 * Get the schema property definition at a specific path
 */
function getSchemaPropertyAtPath(
  schema: typeof resumeSchema,
  path: string[]
): SchemaProperty | null {
  if (path.length === 0) return null;
  
  let current: Record<string, SchemaProperty> | undefined = schema.properties as Record<string, SchemaProperty>;
  let property: SchemaProperty | null = null;

  for (let i = 0; i < path.length; i++) {
    const key = path[i];
    if (!current || !key || !current[key]) return null;

    property = current[key];
    const propType = getPropertyType(property);
    
    // If not the last key, navigate deeper
    if (i < path.length - 1) {
      if (propType === "object" && property.properties) {
        current = property.properties as Record<string, SchemaProperty>;
      } else if (propType === "array") {
        // Handle both direct items and items inside anyOf
        const itemProps = getItemsProperties(property);
        if (itemProps) {
          current = itemProps;
        } else {
          return null;
        }
      } else {
        return null;
      }
    }
  }

  return property;
}

/**
 * Get the key being hovered on a line
 */
function getKeyAtPosition(line: string, column: number): string | null {
  // Match a YAML key (with optional leading dash for array items)
  const keyMatch = line.match(/^\s*-?\s*([a-zA-Z_][a-zA-Z0-9_]*):/);
  if (!keyMatch?.[1]) return null;
  
  const key = keyMatch[1];
  const keyStart = line.indexOf(key);
  const keyEnd = keyStart + key.length;
  
  // Check if cursor is on the key
  if (column >= keyStart + 1 && column <= keyEnd + 1) {
    return key;
  }
  
  return null;
}

/**
 * Get the full path to a key on a specific line
 */
function getPathToKey(text: string, lineNumber: number): string[] {
  const lines = text.split("\n");
  const currentLine = lines[lineNumber - 1] || "";
  const currentIndent = getIndentLevel(currentLine);
  
  // Get context path (parent keys)
  const contextPath = getCurrentContext(text, lineNumber - 1, currentIndent);
  
  // Get the key on the current line
  const keyMatch = currentLine.match(/^\s*-?\s*([a-zA-Z_][a-zA-Z0-9_]*):/);
  if (keyMatch?.[1]) {
    return [...contextPath, keyMatch[1]];
  }
  
  return contextPath;
}

/**
 * Format examples for hover display
 */
function formatExamplesForHover(property: SchemaProperty): string {
  const parts: string[] = [];
  
  // Add title if available
  if (property.title) {
    parts.push(`**${property.title}**`);
  }
  
  // Add description
  if (property.description) {
    parts.push(property.description);
  }
  
  // Get examples - check direct property and anyOf
  let examples: unknown[] = [];
  if (property.examples && property.examples.length > 0) {
    examples = property.examples;
  } else if (property.anyOf) {
    // Find examples in anyOf variants
    for (const variant of property.anyOf) {
      if (variant.examples && variant.examples.length > 0) {
        examples = variant.examples;
        break;
      }
    }
  }
  
  // Add examples section
  if (examples.length > 0) {
    parts.push(""); // Empty line before examples
    parts.push("**Examples:**");
    for (const example of examples.slice(0, 5)) { // Show max 5 examples
      const exampleStr = typeof example === "string" ? example : JSON.stringify(example);
      parts.push(`- \`${exampleStr}\``);
    }
  }
  
  return parts.join("\n\n");
}

// Track if providers are already registered to avoid duplicates
let providersRegistered = false;

function registerProviders(monaco: Monaco) {
  if (providersRegistered) return;
  providersRegistered = true;
  
  // Register hover provider for examples
  monaco.languages.registerHoverProvider("yaml", {
    provideHover: (model: editor.ITextModel, position: Position) => {
      const line = model.getLineContent(position.lineNumber);
      const key = getKeyAtPosition(line, position.column);
      
      if (!key) return null;
      
      // Get full path to this key
      const fullText = model.getValue();
      const path = getPathToKey(fullText, position.lineNumber);
      
      if (path.length === 0) return null;
      
      // Look up schema property
      const property = getSchemaPropertyAtPath(resumeSchema, path);
      
      if (!property) return null;
      
      // Format hover content
      const contents = formatExamplesForHover(property);
      
      if (!contents) return null;
      
      // Get the range of the key for highlighting
      const keyStart = line.indexOf(key);
      const keyEnd = keyStart + key.length;
      
      return {
        range: {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: keyStart + 1,
          endColumn: keyEnd + 1,
        },
        contents: [{ value: contents }],
      };
    },
  });
  
  // Register completion provider
  monaco.languages.registerCompletionItemProvider("yaml", {
    provideCompletionItems: (model: editor.ITextModel, position: Position) => {
      const textUntilPosition = model.getValueInRange({
        startLineNumber: 1,
        startColumn: 1,
        endLineNumber: position.lineNumber,
        endColumn: position.column,
      });

      const fullText = model.getValue();
      const currentLine = model.getLineContent(position.lineNumber);
      const currentIndent = getIndentLevel(currentLine);

      // Check if we're at the start of a line (after indentation) or typing a key
      const lineBeforeCursor = currentLine.substring(0, position.column - 1);
      const isStartOfKey = /^\s*[a-zA-Z_]*$/.test(lineBeforeCursor) || 
                           /^\s*-\s*[a-zA-Z_]*$/.test(lineBeforeCursor);

      if (!isStartOfKey) {
        return { suggestions: [] };
      }

      const word = model.getWordUntilPosition(position);
      const range: IRange = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn,
      };

      // Determine context path
      const path = getCurrentContext(textUntilPosition, position.lineNumber - 1, currentIndent);
      
      // Get schema properties at current path
      let properties: Record<string, SchemaProperty> | null;

      if (path.length === 0) {
        // Root level
        properties = resumeSchema.properties as Record<string, SchemaProperty>;
      } else {
        properties = getSchemaPropertiesAtPath(resumeSchema, path);
      }

      if (!properties) {
        return { suggestions: [] };
      }

      // Get existing keys at this level to filter them out
      const existingKeys = getExistingKeysAtLevel(fullText, currentIndent);

      const suggestions = createCompletionProposals(monaco, range, properties, existingKeys);

      return { suggestions };
    },
  });
}

function handleBeforeMount(monaco: Monaco) {
  registerProviders(monaco);
}

// CSS to swap diff colors and icons (green/+ on left for user content, red/- on right for missing)
// Using global selectors since Monaco creates elements outside the React tree
const swappedColorStyles = `
  /* Ensure diff editor container takes full space */
  .monaco-diff-editor {
    width: 100% !important;
    height: 100% !important;
  }
  
  .monaco-diff-editor > .monaco-editor {
    width: 100% !important;
    height: 100% !important;
  }
  
  /* Left side (original/user's copy): show deletions as green (user has this content) */
  .monaco-editor .line-delete,
  .monaco-editor .char-delete {
    background-color: rgba(155, 185, 85, 0.2) !important;
  }
  .monaco-diff-editor .delete-sign,
  .monaco-diff-editor .diagonal-fill {
    background-color: rgba(155, 185, 85, 0.3) !important;
  }
  
  /* Right side (modified/template): show insertions as red (user needs to add) */
  .monaco-editor .line-insert,
  .monaco-editor .char-insert {
    background-color: rgba(255, 99, 71, 0.2) !important;
  }
  .monaco-diff-editor .insert-sign {
    background-color: rgba(255, 99, 71, 0.3) !important;
  }
  
  /* Swap the gutter icons: - becomes + on left, + becomes - on right */
  .monaco-diff-editor .margin-view-overlays .codicon-diff-remove::before {
    content: "+" !important;
  }
  .monaco-diff-editor .margin-view-overlays .codicon-diff-insert::before {
    content: "−" !important;
  }
  
  /* Alternative approach if codicon classes don't work */
  .monaco-diff-editor .editor.original .margin-view-overlays .diff-review-remove::before,
  .monaco-diff-editor .editor.original .glyph-margin-widgets .codicon::before {
    content: "+" !important;
  }
  .monaco-diff-editor .editor.modified .margin-view-overlays .diff-review-insert::before,
  .monaco-diff-editor .editor.modified .glyph-margin-widgets .codicon::before {
    content: "−" !important;
  }
  
  /* Prevent Safari auto-zoom on mobile by ensuring Monaco textarea has 16px font size */
  @media (max-width: 1023px) {
    .monaco-editor textarea.inputarea {
      font-size: 16px !important;
    }
  }
`;

type ViewMode = "diff" | "pdf";

const PDF_URL = "/pdf/resume-default.pdf";

// Debounce hook for validation
function useDebouncedValidation(
  callback: (content: string) => void,
  delay: number
) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackRef = useRef(callback);
  
  // Keep callback ref updated
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);
  
  return useCallback((content: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => callbackRef.current(content), delay);
  }, [delay]);
}

/**
 * Initialize editor content from localStorage or defaults
 */
function getInitialEditorContent(): { modifiedValue: string; originalValue: string } {
  const storage = getStorage();
  
  // Left editor (modifiedValue): Use activeDraft if exists, otherwise DEFAULT
  const modifiedValue = storage.activeDraft ?? DEFAULT_RESUME_CONTENT;
  
  // Right editor (originalValue): Default to DEFAULT_RESUME_CONTENT
  const originalValue = DEFAULT_RESUME_CONTENT;
  
  return { modifiedValue, originalValue };
}

function YAMLEditor() {
  const [viewMode, setViewMode] = useState<ViewMode>("pdf");
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [isCompiling, setIsCompiling] = useState(false);
  const [compileError, setCompileError] = useState<string | null>(null);
  const [compiledPdfUrl, setCompiledPdfUrl] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showPdfOnMobile, setShowPdfOnMobile] = useState(true);
  
  // Storage-related state
  const [versions, setVersions] = useState<VersionEntry[]>(() => getStorage().versions);
  const [hasLastSavedState, setHasLastSavedState] = useState(() => hasLastSaved());
  
  // Dropdown selection state
  const [editingSource, setEditingSource] = useState<EditingSource>({ type: "current-draft" });
  const [compareSource, setCompareSource] = useState<CompareSource>({ type: "default-template" });
  
  // Template selection state (for PDF view)
  const [selectedTemplate, setSelectedTemplate] = useState<string>("001");
  const availableTemplates = ["001"];
  
  // Version name dialog
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [versionName, setVersionName] = useState("");
  
  // Use refs to store editor instances - avoids re-render issues
  const diffEditorRef = useRef<editor.IStandaloneDiffEditor | null>(null);
  const standardEditorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<Monaco | null>(null);
  
  // Initialize content from storage - only computed once
  const [initialValues] = useState(() => getInitialEditorContent());
  const contentRef = useRef(initialValues.modifiedValue);
  const compareValueRef = useRef(initialValues.originalValue);
  
  // Store viewMode in a ref for stable callbacks
  const viewModeRef = useRef(viewMode);
  useEffect(() => {
    viewModeRef.current = viewMode;
  }, [viewMode]);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Update diff editor renderSideBySide option when mobile state changes
  useEffect(() => {
    if (diffEditorRef.current) {
      diffEditorRef.current.updateOptions({
        renderSideBySide: !isMobile,
        enableSplitViewResizing: !isMobile,
        wordWrap: isMobile ? "on" : "off",
        fontSize: isMobile ? 16 : 14, // Prevent Safari auto-zoom on mobile
      });
      // Force layout update after mode change
      setTimeout(() => {
        diffEditorRef.current?.layout();
      }, 100);
    }
    // Also update standard editor if it exists
    if (standardEditorRef.current) {
      standardEditorRef.current.updateOptions({
        fontSize: isMobile ? 16 : 14, // Prevent Safari auto-zoom on mobile
      });
    }
  }, [isMobile]);

  // Update diff editor when view mode changes to ensure proper rendering
  useEffect(() => {
    if (viewMode === "diff" && diffEditorRef.current) {
      // Update content from refs
      const originalEditor = diffEditorRef.current.getOriginalEditor();
      const modifiedEditor = diffEditorRef.current.getModifiedEditor();
      
      if (originalEditor.getValue() !== contentRef.current) {
        originalEditor.setValue(contentRef.current);
      }
      if (modifiedEditor.getValue() !== compareValueRef.current) {
        modifiedEditor.setValue(compareValueRef.current);
      }
      
      // Force layout update
      setTimeout(() => {
        diffEditorRef.current?.layout();
      }, 50);
    }
  }, [viewMode]);

  // Validation function ref - allows stable callbacks while function can be updated
  const runValidationRef = useRef<(content: string) => void>(() => {});
  
  // Update the validation ref
  runValidationRef.current = (content: string) => {
    const errors = validateYAML(content);
    setValidationErrors(errors);
    
    const monaco = monacoRef.current;
    if (!monaco) return;
    
    // Apply markers to the active editor's model using ref for viewMode
    if (viewModeRef.current === "diff" && diffEditorRef.current) {
      const model = diffEditorRef.current.getOriginalEditor().getModel();
      if (model) setMonacoMarkers(monaco, model, errors);
    } else if (viewModeRef.current === "pdf" && standardEditorRef.current) {
      const model = standardEditorRef.current.getModel();
      if (model) setMonacoMarkers(monaco, model, errors);
    }
  };
  
  // Stable wrapper that calls the ref
  const runValidation = useCallback((content: string) => {
    runValidationRef.current(content);
  }, []);
  
  // Debounced validation (300ms delay)
  const debouncedValidation = useDebouncedValidation(runValidation, 300);

  // Debounced validation ref for stable access in callbacks
  const debouncedValidationRef = useRef(debouncedValidation);
  debouncedValidationRef.current = debouncedValidation;

  // Cleanup effect: properly dispose editors when component unmounts
  useEffect(() => {
    return () => {
      // Dispose diff editor - Monaco will handle model disposal internally
      // We need to dispose it synchronously to prevent the error
      if (diffEditorRef.current) {
        try {
          // Use a small delay to ensure any pending operations complete
          // but dispose synchronously to prevent race conditions
          const editor = diffEditorRef.current;
          diffEditorRef.current = null;
          
          // Dispose the editor - this will properly clean up the widget and models
          editor.dispose();
        } catch (error) {
          // Ignore errors during cleanup - component is unmounting anyway
        }
      }
      
      // Dispose standard editor
      if (standardEditorRef.current) {
        try {
          const editor = standardEditorRef.current;
          standardEditorRef.current = null;
          editor.dispose();
        } catch (error) {
          // Ignore errors during cleanup
        }
      }
      
      // Clear monaco ref
      monacoRef.current = null;
    };
  }, []);

  // Stable mount handlers - empty dependency arrays, use refs for everything
  const handleDiffMount: DiffOnMount = useCallback((diffEditor, monaco) => {
    diffEditorRef.current = diffEditor;
    monacoRef.current = monaco;
    
    // Make the modified (right side - template) editor completely read-only
    const modifiedEditor = diffEditor.getModifiedEditor();
    modifiedEditor.updateOptions({ 
      readOnly: true,
      domReadOnly: true,
    });

    // Track content changes via ref and trigger validation
    const originalEditor = diffEditor.getOriginalEditor();
    
    originalEditor.onDidChangeModelContent(() => {
      const newContent = originalEditor.getValue();
      contentRef.current = newContent;
      debouncedValidationRef.current(newContent);
    });
    
    // Prevent Safari auto-zoom on mobile by ensuring font size is 16px
    if (window.innerWidth < 1024) {
      const ensureFontSize = () => {
        const originalDomNode = originalEditor.getDomNode();
        if (originalDomNode) {
          const textarea = originalDomNode.querySelector('textarea.inputarea') as HTMLTextAreaElement;
          if (textarea) {
            // Ensure font size is at least 16px to prevent zoom
            // This is the primary fix - Safari won't zoom if font size is >= 16px
            textarea.style.fontSize = '16px';
          }
        }
      };
      
      // Set font size initially
      ensureFontSize();
      
      // Ensure font size is maintained on focus (handles re-focus scenarios)
      // Use a focus listener on the editor's DOM node to catch all focus events
      const originalDomNode = originalEditor.getDomNode();
      if (originalDomNode) {
        const handleFocus = () => {
          setTimeout(ensureFontSize, 0);
        };
        originalDomNode.addEventListener('focusin', handleFocus);
      }
    }
    
    // Set initial content from refs
    originalEditor.setValue(contentRef.current);
    modifiedEditor.setValue(compareValueRef.current);
    
    // Run initial validation immediately
    runValidationRef.current(originalEditor.getValue());
    
    // Force layout after a brief delay to ensure proper rendering
    setTimeout(() => {
      diffEditor.layout();
    }, 100);
  }, []);

  const handleEditorMount: OnMount = useCallback((editor, monaco) => {
    standardEditorRef.current = editor;
    monacoRef.current = monaco;
    
    // Track content changes via ref and trigger validation
    editor.onDidChangeModelContent(() => {
      const newContent = editor.getValue();
      contentRef.current = newContent;
      debouncedValidationRef.current(newContent);
    });
    
    // Prevent Safari auto-zoom on mobile by ensuring font size is 16px
    if (window.innerWidth < 1024) {
      const ensureFontSize = () => {
        const editorDomNode = editor.getDomNode();
        if (editorDomNode) {
          const textarea = editorDomNode.querySelector('textarea.inputarea') as HTMLTextAreaElement;
          if (textarea) {
            // Ensure font size is at least 16px to prevent zoom
            // This is the primary fix - Safari won't zoom if font size is >= 16px
            textarea.style.fontSize = '16px';
          }
        }
      };
      
      // Set font size initially
      ensureFontSize();
      
      // Ensure font size is maintained on focus (handles re-focus scenarios)
      // Use a focus listener on the editor's DOM node to catch all focus events
      const editorDomNode = editor.getDomNode();
      if (editorDomNode) {
        const handleFocus = () => {
          setTimeout(ensureFontSize, 0);
        };
        editorDomNode.addEventListener('focusin', handleFocus);
      }
    }
    
    // Run initial validation immediately
    runValidationRef.current(editor.getValue());
  }, []);

  const switchToView = useCallback((newMode: ViewMode) => {
    if (newMode === viewMode) return;
    
    // Get current cursor position from the active editor
    let cursorPosition: { lineNumber: number; column: number } | null = null;
    if (viewMode === "pdf" && standardEditorRef.current) {
      cursorPosition = standardEditorRef.current.getPosition();
    } else if (viewMode === "diff" && diffEditorRef.current) {
      cursorPosition = diffEditorRef.current.getOriginalEditor().getPosition();
    }
    
    // Sync content from the current active editor to the other one
    if (newMode === "pdf" && standardEditorRef.current) {
      // Switching to PDF view - update standard editor with current content
      const currentValue = standardEditorRef.current.getValue();
      if (currentValue !== contentRef.current) {
        standardEditorRef.current.setValue(contentRef.current);
      }
    } else if (newMode === "diff" && diffEditorRef.current) {
      // Switching to Diff view - update diff editor's original side
      const originalEditor = diffEditorRef.current.getOriginalEditor();
      const modifiedEditor = diffEditorRef.current.getModifiedEditor();
      const currentValue = originalEditor.getValue();
      if (currentValue !== contentRef.current) {
        originalEditor.setValue(contentRef.current);
      }
      // Ensure modified side has the correct compare value
      if (modifiedEditor.getValue() !== compareValueRef.current) {
        modifiedEditor.setValue(compareValueRef.current);
      }
    }
    
    setViewMode(newMode);
    
    // Restore cursor position in the new active editor and force layout
    setTimeout(() => {
      if (cursorPosition) {
        if (newMode === "pdf" && standardEditorRef.current) {
          standardEditorRef.current.setPosition(cursorPosition);
          standardEditorRef.current.revealPositionInCenter(cursorPosition);
        } else if (newMode === "diff" && diffEditorRef.current) {
          const originalEditor = diffEditorRef.current.getOriginalEditor();
          originalEditor.setPosition(cursorPosition);
          originalEditor.revealPositionInCenter(cursorPosition);
          // Force layout update for diff editor
          diffEditorRef.current.layout();
        }
      }
      runValidation(contentRef.current);
    }, 50);
  }, [viewMode, runValidation]);

  // Track if we're waiting for iframe to load
  const waitingForIframeLoad = useRef(false);
  
  const handleIframeLoad = useCallback(() => {
    // Only hide loader if we were waiting for this load event
    if (waitingForIframeLoad.current) {
      waitingForIframeLoad.current = false;
      setIsCompiling(false);
    }
  }, []);

  const handleCompile = useCallback(async (skipIframeLoad = false): Promise<string | null> => {
    setIsCompiling(true);
    setCompileError(null);
    
    try {
      const response = await fetch("/api/resume/compile", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ 
          yaml: contentRef.current,
          template: selectedTemplate,
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || "Failed to compile resume");
      }
      
      if (data.success && data.pdfUrl) {
        const currentUrl = compiledPdfUrl ?? PDF_URL;
        // Only wait for iframe load if URL actually changed and we're not skipping it
        if (data.pdfUrl !== currentUrl && !skipIframeLoad) {
          // Mark that we're waiting for the iframe to load before hiding loader
          waitingForIframeLoad.current = true;
          // Set the compiled PDF URL - loader stays visible until iframe loads
          setCompiledPdfUrl(data.pdfUrl);
        } else {
          // URL didn't change or we're skipping iframe load, update URL and hide loader immediately
          setCompiledPdfUrl(data.pdfUrl);
          setIsCompiling(false);
        }
        return data.pdfUrl;
      } else {
        setIsCompiling(false);
        return null;
      }
    } catch (error) {
      setCompileError(error instanceof Error ? error.message : "An error occurred");
      setIsCompiling(false);
      return null;
    }
  }, [compiledPdfUrl, selectedTemplate]);

  // Wrapper for refresh button - calls handleCompile without parameters
  const handleCompileRefresh = useCallback(async () => {
    await handleCompile(false);
  }, [handleCompile]);

  // Handle download - compiles first, then downloads
  const handleDownload = useCallback(async () => {
    // Compile first (skip iframe load since we're downloading)
    const pdfUrl = await handleCompile(true);
    
    // Use the newly compiled URL or fall back to current/default
    const urlToDownload = pdfUrl || compiledPdfUrl || PDF_URL;
    
    // Trigger download
    const link = document.createElement("a");
    link.href = urlToDownload;
    link.download = "resume.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [handleCompile, compiledPdfUrl]);

  // Handle "Save Changes" - updates activeDraft and lastSaved
  const handleSaveChanges = useCallback(() => {
    setIsSaving(true);
    
    try {
      const currentContent = contentRef.current;
      saveChanges(currentContent);
      
      // Update UI state to reflect lastSaved is now available
      setHasLastSavedState(true);
      
      // If currently comparing against "Last Saved", update the right editor imperatively
      if (compareSource.type === "last-saved" && diffEditorRef.current) {
        compareValueRef.current = currentContent;
        diffEditorRef.current.getModifiedEditor().setValue(currentContent);
      }
    } catch (error) {
      console.error("Save changes error:", error);
    } finally {
      setIsSaving(false);
    }
  }, [compareSource.type]);

  // Handle "Save New Version" - opens dialog to name the version
  const handleSaveNewVersionClick = useCallback(() => {
    // Generate a default name
    const defaultName = `Version ${versions.length + 1}`;
    setVersionName(defaultName);
    setVersionDialogOpen(true);
  }, [versions.length]);

  // Confirm saving new version
  const handleConfirmSaveVersion = useCallback(() => {
    if (!versionName.trim()) return;
    
    const currentContent = contentRef.current;
    
    // Save the new named version
    const newVersion = saveNewVersion(versionName.trim(), currentContent);
    
    // Also update lastSaved (same as "Save Changes")
    saveChanges(currentContent);
    setHasLastSavedState(true);
    
    // If currently comparing against "Last Saved", update the right editor
    if (compareSource.type === "last-saved" && diffEditorRef.current) {
      compareValueRef.current = currentContent;
      diffEditorRef.current.getModifiedEditor().setValue(currentContent);
    }
    
    // Re-read versions from storage to ensure state matches storage exactly
    // This prevents duplicates if component re-renders or re-initializes
    setVersions(getVersions());
    
    // Close dialog
    setVersionDialogOpen(false);
    setVersionName("");
  }, [versionName, compareSource.type]);

  // Handle editing source change (left dropdown - restore flow)
  const handleEditingSourceChange = useCallback((source: EditingSource) => {
    if (source.type === "default-template") {
      // Restore from default template
      contentRef.current = DEFAULT_RESUME_CONTENT;
      if (standardEditorRef.current) {
        standardEditorRef.current.setValue(DEFAULT_RESUME_CONTENT);
      }
      if (diffEditorRef.current) {
        diffEditorRef.current.getOriginalEditor().setValue(DEFAULT_RESUME_CONTENT);
      }
      
      // Run validation
      runValidation(DEFAULT_RESUME_CONTENT);
      
      // Reset dropdown back to "Current Draft" - user is now editing a new draft
      setEditingSource({ type: "current-draft" });
    } else if (source.type === "version") {
      // Restore from version: load version content into editor
      const version = getVersionById(source.versionId);
      if (version) {
        // Update editor content
        contentRef.current = version.content;
        if (standardEditorRef.current) {
          standardEditorRef.current.setValue(version.content);
        }
        if (diffEditorRef.current) {
          diffEditorRef.current.getOriginalEditor().setValue(version.content);
        }
        
        // Run validation
        runValidation(version.content);
      }
      
      // Reset dropdown back to "Current Draft" - user is now editing a new draft
      setEditingSource({ type: "current-draft" });
    } else {
      // Just selecting current draft - no action needed
      setEditingSource(source);
    }
  }, [runValidation]);

  // Handle compare source change (right dropdown)
  const handleCompareSourceChange = useCallback((source: CompareSource) => {
    setCompareSource(source);
    
    let newCompareValue = DEFAULT_RESUME_CONTENT;
    
    if (source.type === "default-template") {
      newCompareValue = DEFAULT_RESUME_CONTENT;
    } else if (source.type === "last-saved") {
      const storage = getStorage();
      newCompareValue = storage.lastSaved ?? DEFAULT_RESUME_CONTENT;
    } else if (source.type === "version") {
      const version = getVersionById(source.versionId);
      newCompareValue = version?.content ?? DEFAULT_RESUME_CONTENT;
    }
    
    // Update the ref and the diff editor's modified (right) side imperatively
    compareValueRef.current = newCompareValue;
    if (diffEditorRef.current) {
      diffEditorRef.current.getModifiedEditor().setValue(newCompareValue);
      // Force layout update after content change
      setTimeout(() => {
        diffEditorRef.current?.layout();
      }, 50);
    }
  }, []);

  return (
    <div className="flex flex-col h-screen bg-zinc-950">
      <TopBar
        viewMode={viewMode}
        onViewModeChange={switchToView}
        isCompiling={isCompiling}
        compileError={compileError}
        hasValidationErrors={validationErrors.some(e => e.severity === "error")}
        onCompile={handleCompileRefresh}
        onDownload={handleDownload}
        onSaveChanges={handleSaveChanges}
        onSaveNewVersion={handleSaveNewVersionClick}
        isSaving={isSaving}
        showPdfOnMobile={showPdfOnMobile}
        onTogglePdfVisibility={() => setShowPdfOnMobile(!showPdfOnMobile)}
        editingSource={editingSource}
        onEditingSourceChange={handleEditingSourceChange}
        compareSource={compareSource}
        onCompareSourceChange={handleCompareSourceChange}
        selectedTemplate={selectedTemplate}
        onTemplateChange={setSelectedTemplate}
        availableTemplates={availableTemplates}
        versions={versions}
        hasLastSaved={hasLastSavedState}
        pdfUrl={compiledPdfUrl}
      />

      {/* Save New Version Dialog */}
      <Dialog open={versionDialogOpen} onOpenChange={setVersionDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-white">Save New Version</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Give this version a name to help you identify it later.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="version-name" className="text-zinc-300">
                Version Name
              </Label>
              <Input
                id="version-name"
                value={versionName}
                onChange={(e) => setVersionName(e.target.value)}
                placeholder="e.g., Google Application"
                className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && versionName.trim()) {
                    handleConfirmSaveVersion();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setVersionDialogOpen(false)}
              className="text-zinc-400 hover:text-white hover:bg-zinc-800 cursor-pointer"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSaveVersion}
              disabled={!versionName.trim()}
              className="bg-emerald-600 hover:bg-emerald-500 text-white cursor-pointer"
            >
              Save Version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Main Content - Both views are always mounted, visibility controlled by CSS */}
      <div className="flex-1 relative overflow-hidden flex flex-col">
        {/* Editor Area */}
        <div className="flex-1 relative">
        {/* Diff View */}
        <div 
          className={`absolute inset-0 w-full h-full ${viewMode === "diff" ? "visible" : "invisible pointer-events-none"}`}
        >
          <style>{swappedColorStyles}</style>
          <div className="w-full h-full">
            <DiffEditor
              height="100%"
              width="100%"
              language="yaml"
              original={contentRef.current}
              modified={compareValueRef.current}
              beforeMount={handleBeforeMount}
              onMount={handleDiffMount}
              theme="vs-dark"
              options={{
                minimap: { enabled: false },
                fontSize: isMobile ? 16 : 14, // Prevent Safari auto-zoom on mobile
                lineNumbers: "off",
                renderSideBySide: !isMobile,
                originalEditable: true,
                readOnly: true,
                enableSplitViewResizing: !isMobile,
                padding: { top: 8, bottom: 8 },
                automaticLayout: true,
                wordWrap: isMobile ? "on" : "off",
              }}
            />
          </div>
        </div>

        {/* PDF View */}
        <div 
          className={`absolute inset-0 flex flex-col lg:flex-row ${viewMode === "pdf" ? "visible" : "invisible pointer-events-none"}`}
        >
          {/* Left: YAML Editor */}
          <div className={`w-full ${showPdfOnMobile ? "lg:w-1/2 h-1/2" : "h-full"} lg:h-full border-b lg:border-b-0 lg:border-r border-zinc-800`}>
            <Editor
              height="100%"
              language="yaml"
              defaultValue={initialValues.modifiedValue}
              beforeMount={handleBeforeMount}
              onMount={handleEditorMount}
              theme="vs-dark"
              options={{
                minimap: { enabled: false },
                fontSize: isMobile ? 16 : 14, // Prevent Safari auto-zoom on mobile
                lineNumbers: "off",
                padding: { top: 8 },
                wordWrap: "on",
              }}
            />
          </div>
          {/* Right: PDF Viewer */}
          <div className={`w-full lg:w-1/2 ${showPdfOnMobile ? "h-1/2" : "hidden"} lg:h-full bg-zinc-800 relative`}>
            {isCompiling && (
              <div className="absolute inset-0 bg-zinc-900/90 flex flex-col items-center justify-center z-10 backdrop-blur-sm">
                <div className="flex flex-col items-center gap-4">
                  <div className="relative">
                    <div className="w-12 h-12 border-4 border-zinc-700 border-t-emerald-500 rounded-full animate-spin" />
                  </div>
                  <span className="text-zinc-300 text-sm font-medium">Compiling your resume...</span>
                  <div className="flex gap-1 mt-2">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}
            <iframe
              src={compiledPdfUrl ?? PDF_URL}
              title="Resume PDF"
              className="w-full h-full border-0"
              onLoad={handleIframeLoad}
            />
          </div>
        </div>
        </div>
        
        {/* Validation Error Panel */}
        {validationErrors.length > 0 && (
          <div className="border-t border-zinc-800 bg-zinc-900">
            <div className="flex items-center gap-2 px-2 sm:px-4 py-2 border-b border-zinc-800">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="14" 
                height="14" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                className="text-red-400 shrink-0"
              >
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <span className="text-xs sm:text-sm font-medium text-zinc-300">
                Problems ({validationErrors.length})
              </span>
            </div>
            <div className="max-h-32 overflow-y-auto">
              {validationErrors.map((error, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-2 sm:gap-3 px-2 sm:px-4 py-2 hover:bg-zinc-800/50 cursor-pointer text-xs sm:text-sm"
                  onClick={() => {
                    // Navigate to error location in editor
                    const editor = viewMode === "diff" 
                      ? diffEditorRef.current?.getOriginalEditor()
                      : standardEditorRef.current;
                    if (editor) {
                      editor.setPosition({ lineNumber: error.line, column: error.column });
                      editor.revealLineInCenter(error.line);
                      editor.focus();
                    }
                  }}
                >
                  <span className={`mt-0.5 shrink-0 ${error.severity === "error" ? "text-red-400" : "text-yellow-400"}`}>
                    {error.severity === "error" ? "✕" : "⚠"}
                  </span>
                  <span className="flex-1 text-zinc-300 break-words">{error.message}</span>
                  <span className="text-zinc-500 font-mono text-[10px] sm:text-xs shrink-0 ml-1">
                    L{error.line}:{error.column}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Status Bar */}
        <div className="h-6 px-2 sm:px-4 flex items-center justify-between bg-zinc-800 border-t border-zinc-700 text-[10px] sm:text-xs">
          <div className="flex items-center gap-2 sm:gap-4">
            <span className="text-zinc-400 hidden sm:inline">YAMLResume v0.8.0</span>
            <span className="text-zinc-400 sm:hidden">v0.8.0</span>
            {validationErrors.length === 0 ? (
              <span className="flex items-center gap-1 text-emerald-400">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Valid
              </span>
            ) : (
              <span className="flex items-center gap-1 text-red-400">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
                {validationErrors.filter(e => e.severity === "error").length} errors
                {validationErrors.filter(e => e.severity === "warning").length > 0 && (
                  <>, {validationErrors.filter(e => e.severity === "warning").length} warnings</>
                )}
              </span>
            )}
          </div>
          <span className="text-zinc-500">YAML</span>
        </div>
      </div>
    </div>
  );
}

export default YAMLEditor;
